import React, { useEffect, useState } from "react";
import { FaPlus, FaInfoCircle, FaCheck } from "react-icons/fa";
import { Modal, FormControl } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import {
  UpdateselectionGroupName,
  UpdateselectionGroupDescription,
  ToggleSeparatePrintingBySelectionGroupId,
} from '../../../../../../crud/menu_api/selection_groups';
import { toast } from 'react-toastify';
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner';
import AddItemToGroupModal from "./components/AddItemToGroupModal/AddItemToGroupModal";
import './GroupDetail.scss';
import GroupItemsTable from './components/GroupItemsTable/GroupItemsTable';
import ItemGroupsTable from "./components/ItemGroupTable/ItemGroupTable";

interface SelectionGroupType {
  readonly selection_group_id: number;
  selection_group_name: string;
  selection_group_description?: string;
  restaurant_id?: number;
  local_id: number;
  separate_printing?: boolean;
  items_affected?: Array<{
    item_id: number;
    item_name: string;
  }>;
  items_contained?: Array<{
    item_id: number;
    item_name: string;
  }>;
}

interface SelectionItemType {
  selection_group_id: number;
  item_id: number;
  label: string;
  item_name: string;
  desk_price_modifier: number;
  delivery_price_modifier: number;
  online_price_modifier: number;
  price_modifier: number;
  min_selections: number;
  max_selections: number;
}

interface GroupDetailProps {
  show: boolean;
  setShow: (show: boolean) => void;
  sg: SelectionGroupType;
  data: SelectionGroupType[];
  setData: (data: SelectionGroupType[]) => void;
}

const GroupDetail: React.FC<GroupDetailProps> = ({ show, setShow, sg, data, setData }) => {
  const [items, setItems] = useState<SelectionItemType[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addingItems, setAddingItems] = useState(false);

  // Nuevo estado para manejar el título en tiempo real
  const [currentName, setCurrentName] = useState<string>(sg.selection_group_name);
  const [draftName, setDraftName] = useState<string>(sg.selection_group_name);
  const [currentDescription, setCurrentDescription] = useState<string>(sg.selection_group_description || '');
  const [draftDescription, setDraftDescription] = useState<string>(sg.selection_group_description || '');
  const [separatePrinting, setSeparatePrinting] = useState<boolean>(sg.separate_printing || false);
  const [viewMode, setViewMode] = useState<'selectionItems' | 'ItemSelections'>('selectionItems');

  // useEffect para simular carga inicial de datos
  useEffect(() => {
    if (show) {
      setLoading(true);
      // Simular carga de datos - reemplaza esto con tu API call real
      const loadData = async () => {
        try {
          // Aquí irían tus llamadas a la API para cargar los productos
          // Por ejemplo: await fetchGroupItems(sg.selection_group_id);
          
          // Simulación de delay para mostrar el loading
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          setLoading(false);
        } catch (error) {
          console.error('Error cargando datos:', error);
          toast.error('Error al cargar los datos del grupo');
          setLoading(false);
        }
      };
      
      loadData();
    }
  }, [show, sg.selection_group_id]);

  // Cuando cambia el prop sg.selection_group_name (por ejemplo desde fuera), reseteamos ambos estados
  useEffect(() => {
    setCurrentName(sg.selection_group_name);
    setDraftName(sg.selection_group_name);
    setCurrentDescription(sg.selection_group_description || '');
    setDraftDescription(sg.selection_group_description || '');
    setSeparatePrinting(sg.separate_printing || false);
  }, [sg.selection_group_name, sg.selection_group_description, sg.separate_printing]);

  // Handler to update parent data when items are added to the group
  const handleItemsAdded = (newItems: any[]) => {
    // Update the parent's data array with the new items_contained
    const updatedData = data.map(group => {
      if (group.selection_group_id === sg.selection_group_id) {
        const currentContained = group.items_contained || [];
        const newContainedItems = newItems.map(item => ({
          item_id: item.item_id,
          item_name: item.item_name
        }));
        return {
          ...group,
          items_contained: [...currentContained, ...newContainedItems]
        };
      }
      return group;
    });
    setData(updatedData);
  };

  // Handler to update parent data when an item is removed from the group
  const handleItemRemoved = (itemId: number) => {
    // Update the parent's data array by removing the item from items_contained
    const updatedData = data.map(group => {
      if (group.selection_group_id === sg.selection_group_id) {
        const currentContained = group.items_contained || [];
        return {
          ...group,
          items_contained: currentContained.filter(item => item.item_id !== itemId)
        };
      }
      return group;
    });
    setData(updatedData);
  };

  const persistName = () => {
    if (loading) return; // No permitir acciones durante loading
    
    const trimmed = draftName.trim();
    if (trimmed === "") {
      toast.error('El nombre del grupo no puede estar vacío.');
      return;
    }
    if (trimmed === currentName) return; // no changes
    UpdateselectionGroupName({ selection_group_name: trimmed }, sg.selection_group_id)
      .then(() => {
        toast.success('Nombre del grupo actualizado.');
        const updatedData = data.map(group => group.selection_group_id === sg.selection_group_id ? { ...group, selection_group_name: trimmed } : group);
        setData(updatedData);
        setCurrentName(trimmed);
      })
      .catch(error => {
        console.error('Error actualizando el nombre del grupo', error);
        const message = error.response?.data?.message || 'Error al actualizar el nombre del grupo';
        toast.error(message);
        setDraftName(currentName); // revert
      });
  };

  const persistDescription = () => {
    if (loading) return;
    
    const trimmed = draftDescription.trim();
    if (trimmed === currentDescription) return; // no changes
    
    UpdateselectionGroupDescription({ selection_group_description: trimmed }, sg.selection_group_id)
      .then(() => {
        toast.success('Descripción del grupo actualizada.');
        const updatedData = data.map(group => 
          group.selection_group_id === sg.selection_group_id 
            ? { ...group, selection_group_description: trimmed } 
            : group
        );
        setData(updatedData);
        setCurrentDescription(trimmed);
      })
      .catch(error => {
        console.error('Error actualizando la descripción del grupo', error);
        const message = error.response?.data?.message || 'Error al actualizar la descripción del grupo';
        toast.error(message);
        setDraftDescription(currentDescription); // revert
      });
  };

  const handleSeparatePrintingToggle = () => {
    if (loading) return; // No permitir acciones durante loading
    
    ToggleSeparatePrintingBySelectionGroupId(sg.selection_group_id)
      .then(() => {
        const newValue = !separatePrinting;
        setSeparatePrinting(newValue);
        toast.success(`Impresión separada ${newValue ? 'habilitada' : 'deshabilitada'}.`);
        
        // Update the data in the parent component
        const updatedData = data.map(group => 
          group.selection_group_id === sg.selection_group_id 
            ? { ...group, separate_printing: newValue } 
            : group
        );
        setData(updatedData);
      })
      .catch(error => {
        console.error('Error actualizando impresión separada', error);
        const message = error.response?.data?.message || 'Error al actualizar la configuración de impresión';
        toast.error(message);
      });
  };

  const renderViewSelector = () => {
    const selectionCount = items.length;
    const affectedCount = sg.items_affected?.length || 0;
    return (
      <div className="gd-toggle">
        <button
          type="button"
          className={`pill ${viewMode === 'selectionItems' ? 'active' : ''}`}
          onClick={() => setViewMode('selectionItems')}
          disabled={loading}
        >
          Componentes <span className="count">{selectionCount}</span>
        </button>
        <button
          type="button"
          className={`pill ${viewMode === 'ItemSelections' ? 'active' : ''}`}
          onClick={() => setViewMode('ItemSelections')}
          disabled={loading}
        >
          Vinculado a <span className="count">{affectedCount}</span>
        </button>
        <div className="pill-glider" aria-hidden="true" />
      </div>
    );
  };

  const renderGroupItems = () => (
    <div className="gd-section">
      <div className="gd-section-header">
        <div className="section-info">
          <h4>Productos dentro del grupo</h4>
          <p className="hint">Estos son los ítems que el cliente podrá elegir cuando este grupo esté disponible.</p>
        </div>
        <button 
          className="action-btn" 
          onClick={() => setShowCreateModal(true)}
          disabled={loading}
        >
          <FaPlus /> Añadir producto
        </button>
      </div>
      <div className="table-wrapper compact-scroll" style={{ position: 'relative' }}>
        {loading ? (
          <div className="loading-container">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <p>Cargando productos...</p>
          </div>
        ) : (
          <>
            {addingItems && (
              <div className="table-loading-overlay">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span className="loading-text">Añadiendo productos...</span>
              </div>
            )}
            <GroupItemsTable items={items} setItems={setItems} sg={sg} onItemRemoved={handleItemRemoved} />
          </>
        )}
      </div>
      {showCreateModal && !loading && (
        <AddItemToGroupModal
          show={showCreateModal}
          handleClose={() => setShowCreateModal(false)}
          sg={sg}
          setGroupItems={setItems}
          groupItems={items}
          setAddingItems={setAddingItems}
          onItemsAdded={handleItemsAdded}
        />
      )}
    </div>
  );

  // Handler provided by child table to open its add modal externally
  const [openAddLinked, setOpenAddLinked] = useState<(() => void) | null>(null);

  const renderItemGroups = () => (
    <div className="gd-section">
      <div className="gd-section-header">
        <div className="section-info">
          <h4>Productos vinculados</h4>
          <p className="hint">Estos productos muestran este grupo como opción para el cliente.</p>
        </div>
        {openAddLinked && !loading && (
          <button 
            className="action-btn" 
            onClick={() => openAddLinked()}
            disabled={loading}
          >
            <FaPlus /> Agregar ítem base
          </button>
        )}
      </div>
      <div className="table-wrapper table-wrapper-large">
        {loading ? (
          <div className="loading-container">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <p>Cargando productos vinculados...</p>
          </div>
        ) : (
          <ItemGroupsTable sg={sg} registerAddHandler={(fn)=>setOpenAddLinked(()=>fn)} />
        )}
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      className="selection-groups-details-modal"
      scrollable
      fullscreen="sm-down"
    >
      <Modal.Header className="gd-header">
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">Configuración de grupo</h2>
        </div>
        <button type="button" className="modal-close-button" aria-label="Cerrar" onClick={() => setShow(false)} disabled={loading}>
          <X size={24} />
        </button>
      </Modal.Header>

      <Modal.Body className="gd-body">
        <div className="gd-inline-name">
          <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
            <FormControl
              type="text"
              value={draftName}
              onChange={(e) => { setDraftName(e.target.value); }}
              onKeyDown={(e) => { if (e.key === 'Escape') { setDraftName(currentName); } if (e.key === 'Enter' && draftName.trim() !== '' && draftName.trim() !== currentName.trim() && !loading) { persistName(); } }}
              className="gd-name-input always-edit"
              maxLength={60}
              placeholder="Nombre del grupo"
              aria-label="Nombre del grupo"
              autoFocus
              disabled={loading}
            />
            {draftName.trim() !== '' && draftName.trim() !== currentName.trim() && (
              <button type="button" className="btn form-button-create" onClick={persistName} aria-label="Guardar nombre del grupo" disabled={loading}>
                <FaCheck style={{ marginRight:6 }} /> Guardar
              </button>
            )}
          </div>
        </div>

        {/* Campo de descripción */}
        <div className="gd-inline-description">
          <div style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
            <FormControl
              as="textarea"
              rows={2}
              value={draftDescription}
              onChange={(e) => { setDraftDescription(e.target.value); }}
              onKeyDown={(e) => { if (e.key === 'Escape') { setDraftDescription(currentDescription); } }}
              onBlur={() => { if (draftDescription.trim() !== currentDescription && !loading) { persistDescription(); } }}
              className="gd-description-input"
              maxLength={255}
              placeholder="Descripción del grupo (opcional)"
              aria-label="Descripción del grupo"
              disabled={loading}
            />
            {draftDescription.trim() !== currentDescription && (
              <button type="button" className="btn form-button-create" onClick={persistDescription} aria-label="Guardar descripción del grupo" disabled={loading}>
                <FaCheck style={{ marginRight:6 }} /> Guardar
              </button>
            )}
          </div>
        </div>
        
        {/* Separate Printing Section */}
        <div className="gd-separate-printing">
          <div className="gd-separate-printing-header">
            <div className="setting-info">
              <h4>Impresión Separada</h4>
              <p className="hint">Genera comandas separadas para cada ítem del grupo</p>
            </div>
            <label className="gd-switch" htmlFor="separate-printing-toggle">
              <input 
                type="checkbox" 
                id="separate-printing-toggle"
                checked={separatePrinting}
                onChange={handleSeparatePrintingToggle}
                disabled={loading}
              />
              <span className="gd-slider"></span>
            </label>
          </div>
          <div className="gd-separate-printing-info">
            <FaInfoCircle className="info-icon" />
            <div className="info-text">
              {separatePrinting ? (
                <p><strong>Habilitado:</strong> Se crearán comandas separadas. Ejemplo: "Hamburguesa" y "Papas" como dos órdenes independientes.</p>
              ) : (
                <p><strong>Deshabilitado:</strong> Se enviará como una sola comanda. Ejemplo: "Hamburguesa con Papas" a la estación principal.</p>
              )}
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="modal-loading-overlay">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <p>Cargando configuración del grupo...</p>
          </div>
        ) : (
          <>
            {viewMode === 'selectionItems' && (
              <div className="gd-info-banner" role="note">
                <FaInfoCircle className="banner-icon" />
                <div className="banner-text">
                  <p><strong>Añade productos reales</strong> que el cliente podrá elegir. No agregues estados como <em>caliente/frío</em>; esos van en un <strong>Grupo Modificador</strong>.</p>
                </div>
              </div>
            )}
            {renderViewSelector()}
            {viewMode === 'selectionItems' ? renderGroupItems() : renderItemGroups()}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <button type="button" className="btn form-button-cancel" onClick={() => setShow(false)} disabled={loading}>Cerrar</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default GroupDetail;
